import type { Event } from '@sentry/core';

/** The document an event was captured on. */
export function documentUrlOf(event: Event): string | undefined {
  // Cast, not a DOM reference: this lib typechecks without the DOM lib in scope.
  return event.request?.url ?? (globalThis as { location?: { href?: string } }).location?.href;
}

/** A frame's filename resolved against the document it ran in. */
export function resolveFrameUrl(filename: string, documentUrl?: string | URL): URL | undefined {
  try {
    return new URL(filename, documentUrl);
  } catch {
    return undefined;
  }
}

/**
 * Read at module init, before the router can navigate: an inline script keeps the URL the document loaded
 * with as its frame filename, while `location.href` moves on every client-side navigation.
 */
const loadedDocumentPage = resolveFrameUrl((globalThis as { location?: { href?: string } }).location?.href ?? '');

/** Pathnames that still mean "the document" on its own origin. */
export function documentPathnames(documentPage: URL | undefined): Set<string> {
  const pathnames = new Set<string>();
  if (documentPage) {
    pathnames.add(documentPage.pathname);
  }
  if (loadedDocumentPage && (!documentPage || loadedDocumentPage.origin === documentPage.origin)) {
    pathnames.add(loadedDocumentPage.pathname);
  }
  return pathnames;
}

/** True when a frame points at the document itself, i.e. inline or injected code. */
export function isDocumentFrame(filename: string, documentUrl: string): boolean {
  const documentPage = resolveFrameUrl(documentUrl);
  const framePage = resolveFrameUrl(filename, documentUrl);
  if (!documentPage || !framePage) {
    return false;
  }
  return framePage.origin === documentPage.origin && documentPathnames(documentPage).has(framePage.pathname);
}
